// Front/src/pages/NewClient.js

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../hooks/useAuthContext";
import { toast, ToastContainer } from "react-toastify";
import { generateClientJson } from "../utils/generateClientJson"; // for generating the json
import { upload_client } from "../api"; // for uploading the short json to the webapi
import { useLanguageContext } from '../context/LanguageContext'; // for using right to left and left to right
import { useTranslate } from '../hooks/useTranslate'; // for translating to hebrew and english
import '../style/NewClient.css';


const NewClient = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthContext(); // still unused, can be removed or kept for future use

  const t = useTranslate(); // Get translations
  const { language } = useLanguageContext(); // Get current language

  // store the user input
  const raw = localStorage.getItem("newClientFormData");
  let savedClientData = {};
  try {
    const parsed = JSON.parse(raw);
    // for debugging:
    // console.log("parsed?.DOCNO = ", parsed?.DOCNO);
    // console.log("location?.state?.DOCNO = ", location?.state?.DOCNO);
    if (parsed?.DOCNO === location?.state?.DOCNO) {
      savedClientData = parsed;
    } else {
      localStorage.removeItem("newClientFormData");
    }
  } catch (e) {
    // In case JSON is corrupted
    localStorage.removeItem("newClientFormData");
    console.warn("Invalid localStorage.newClientFormData", e);
  }

  // Prefill fields if passed from previous screen (like custId)
  const [formData, setFormData] = useState({
    clientId: location.state?.custId || "",
    name: location.state?.CDES || "",
    address: location.state?.ZMOZ_ADDRESS || "",
    nameEng: "",
    addressEng: "",
    phone: location.state?.PHONENUM || "",
    fax: "",
    email: location.state?.ZMOZ_EMAIL || "",
    contactName: location.state?.NAME || "",
    contactNameEng: "",
    contactEmail: location.state?.ZMOZ_EMAIL || "",
    contactPhone: location.state?.PHONENUM || "",
    departmentName: "",            // NEW department
    departmentPerson: "",          // NEW department
    DOCNO: location.state?.DOCNO || "",
    ...savedClientData // restore previously saved form
  });

  useEffect(() => {
    localStorage.setItem("newClientFormData", JSON.stringify(formData));
  }, [formData]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const handlePopState = () => {
      localStorage.removeItem("newClientFormData");
    };
  
    window.addEventListener("popstate", handlePopState);
  
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.clientId || !formData.address) {
      toast.error(t.fillAllClient, {        
        icon: false, // Removes the default tick icon
        autoClose: 3000, // 3 seconds (default)
      });
      return;
    }

    try {
        const json = generateClientJson(formData, user?.id || null); // build the payload
        // for debugging
        //console.log("Submitting client JSON:", json); // Optional debug
    
        const response = await upload_client(json, user?.id || null); // actual upload
    
        if (response?.status === 200) {
          localStorage.removeItem("newClientFormData");
          toast.success(t.clientAddedSuccess, {
            autoClose: 3000, // 3 seconds (default)
            icon: false, // Removes the default tick icon
            onClose: () => { navigate(-1); }
          });        
        } else {
          const msg = response?.error?.response?.data || t.unknownError;
          toast.error(t.clientAddedFailed + msg, {    
            icon: false, // Removes the default tick icon
            autoClose: 3000, // 3 seconds (default)
          });
        }
      } catch (error) {
        console.error(t.clientAddedFailed, error);
        toast.error(t.clientAddedFailed, {       
          icon: false, // Removes the default tick icon
          autoClose: 3000, // 3 seconds (default)
        });
      }
    };


  return (
    <div className="new-client-container" dir={language === 'he' ? 'rtl' : 'ltr'}>

      <button 
          type="button" 
          className="button-go-back"
          onClick={() => {
              localStorage.removeItem("newClientFormData");
              navigate(-1);
          }}
      >
          { t.goBack }
      </button>

      <h2>{t.createClientTitle}</h2>
      <form onSubmit={handleSubmit} className="new-client-form">
      {/* PopUp Notifications using Toast */}
      <ToastContainer
          position="top-center"
          autoClose={3000}
          toastClassName="custom-toast"
          closeButton={true} // use default button
      />

        <label>{t.clientNumber}</label>
        <input name="clientId" value={formData.clientId} onChange={handleChange} required className="numeric-input" />

        <label>{t.clientName}</label>
        <input name="name" value={formData.name} onChange={handleChange} required />

        <label>{t.address}</label>
        <input name="address" value={formData.address} onChange={handleChange} required />

        <label>{t.clientNameEngLabel}</label>
        <input name="nameEng" value={formData.nameEng} onChange={handleChange} className="numeric-input" />

        <label>{t.clientAddressEngLabel}</label>
        <input name="addressEng" value={formData.addressEng} onChange={handleChange} className="numeric-input" />

        <label>{t.phoneLabel}</label>
        <input name="phone" value={formData.phone} onChange={handleChange} className="numeric-input" />

        <label>{t.faxLabel}</label>
        <input name="fax" value={formData.fax} onChange={handleChange} className="numeric-input" />

        <label>{t.emailLabel}</label>
        <input name="email" value={formData.email} onChange={handleChange} className="numeric-input" />

        <h3 className="section-title">{t.contactSectionTitle}</h3>

        <label>{t.contactNameLabel}</label>
        <input name="contactName" value={formData.contactName} onChange={handleChange} />

        <label>{t.contactNameEngLabel}</label>
        <input name="contactNameEng" value={formData.contactNameEng} onChange={handleChange} className="numeric-input" />

        <label>{t.contactEmailLabel}</label>
        <input name="contactEmail" value={formData.contactEmail} onChange={handleChange} className="numeric-input" />

        <label>{t.contactPhoneLabel}</label>
        <input name="contactPhone" value={formData.contactPhone} onChange={handleChange} className="numeric-input" type="number" />

        <h3 className="section-title">{t.departmentSectionTitle}</h3>

        <label>{t.departmentNameLabel}</label>
        <input name="departmentName" value={formData.departmentName} onChange={handleChange} />

        <label>{t.departmentPersonLabel}</label>
        <input name="departmentPerson" value={formData.departmentPerson} onChange={handleChange} />

        <button type="submit" className="submit-button">
          {t.submitClient}
        </button>
      </form>
    </div>
  );
};

export default NewClient;
